import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatDateTime, parseDecimal } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";

function NewSaleDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([]);
  const [payment, setPayment] = useState("DINHEIRO");
  const [notes, setNotes] = useState("");
  const [pick, setPick] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/items", { params: { status: "DISPONIVEL" } });
        setItems(data.filter((i) => i.qty_available > 0));
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [open]);

  const addLine = (itemId) => {
    const it = items.find((i) => i.id === itemId);
    if (!it || lines.some((l) => l.item_id === itemId)) return;
    setLines([...lines, { item_id: it.id, code: it.internal_code, desc: it.description, max: it.qty_available, quantity: 1, sold_price: it.current_price, discount: 0 }]);
    setPick("");
  };
  const upd = (i, k, v) => { const c = [...lines]; c[i] = { ...c[i], [k]: v }; setLines(c); };
  const rm = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + (parseDecimal(l.sold_price) || 0) * (parseInt(l.quantity) || 0) - (parseDecimal(l.discount) || 0), 0);

  const submit = async () => {
    if (lines.length === 0) { toast.error("Adicione ao menos uma mercadoria"); return; }
    setSaving(true);
    try {
      await api.post("/sales", {
        lines: lines.map((l) => ({ item_id: l.item_id, quantity: parseInt(l.quantity) || 1, sold_price: parseDecimal(l.sold_price) || 0, discount: parseDecimal(l.discount) || 0 })),
        payment_method: payment, notes,
      });
      toast.success("Venda registrada");
      setOpen(false); setLines([]); setNotes(""); setPayment("DINHEIRO");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="vendas-new-button"><Plus className="h-4 w-4 mr-1" /> Nova venda</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Registrar venda</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Adicionar mercadoria</Label>
            <Select value={pick} onValueChange={addLine}>
              <SelectTrigger data-testid="venda-item-select"><SelectValue placeholder="Selecione uma peça disponível" /></SelectTrigger>
              <SelectContent>
                {items.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma peça disponível</div>}
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.internal_code} · {i.description} ({formatBRL(i.current_price)})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {lines.length > 0 && (
            <div className="rounded-lg border border-border divide-y divide-border">
              {lines.map((l, i) => (
                <div key={l.item_id} className="p-3 flex items-center gap-2" data-testid={`venda-line-${l.code}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-num text-sm font-semibold truncate">{l.code}</p>
                    <p className="text-xs text-muted-foreground truncate">{l.desc}</p>
                  </div>
                  <div className="w-16"><Label className="text-[10px]">Qtd</Label><Input type="number" min="1" max={l.max} value={l.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className="h-8" /></div>
                  <div className="w-24"><Label className="text-[10px]">Preço</Label><Input type="text" inputMode="decimal" value={l.sold_price} onChange={(e) => upd(i, "sold_price", e.target.value)} className="h-8" data-testid={`venda-price-${l.code}`} /></div>
                  <div className="w-24"><Label className="text-[10px]">Desconto</Label><Input type="text" inputMode="decimal" value={l.discount} onChange={(e) => upd(i, "discount", e.target.value)} className="h-8" /></div>
                  <Button size="icon" variant="ghost" onClick={() => rm(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Forma de pagamento</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger data-testid="venda-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                  <SelectItem value="PIX">Pix</SelectItem>
                  <SelectItem value="CARTAO_DEBITO">Cartão débito</SelectItem>
                  <SelectItem value="CARTAO_CREDITO">Cartão crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Observações</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
            <span className="section-label">Total líquido</span>
            <span className="font-num text-xl font-bold" data-testid="venda-total">{formatBRL(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="venda-form-submit">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirmar venda</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Sales() {
  const { currentOrg } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales"); setSales(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const cancel = async (id) => {
    try { await api.post(`/sales/${id}/cancel`); toast.success("Venda cancelada e estoque estornado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Vendas</h1>
          <p className="text-muted-foreground mt-1">Cada venda é vinculada automaticamente ao lote de origem</p>
        </div>
        <NewSaleDialog onDone={load} />
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : sales.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground">Nenhuma venda registrada.</p>
        ) : (
          <Table data-testid="vendas-table">
            <TableHeader><TableRow>
              <TableHead>Venda</TableHead><TableHead>Data/hora</TableHead><TableHead>Itens</TableHead><TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">Líquido</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sales.map((s) => (
                <TableRow key={s.id} data-testid={`venda-row-${s.sale_number}`}>
                  <TableCell className="font-num font-semibold">{s.sale_number}</TableCell>
                  <TableCell>{formatDateTime(s.timestamp)}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground">{s.lines?.map((l) => l.item_code).join(", ")}</TableCell>
                  <TableCell>{s.payment_method}</TableCell>
                  <TableCell className="text-right font-num">{formatBRL(s.total_gross)}</TableCell>
                  <TableCell className="text-right font-num font-semibold">{formatBRL(s.total_net)}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded ${s.status === "CANCELADA" ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{s.status === "CANCELADA" ? "Cancelada" : "Confirmada"}</span></TableCell>
                  <TableCell>
                    {s.status !== "CANCELADA" && <Button size="sm" variant="ghost" onClick={() => cancel(s.id)} data-testid={`venda-cancel-${s.sale_number}`}><Trash2 className="h-4 w-4 text-red-500 mr-1" />Cancelar</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
